import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  DownloadOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusOutlined,
  StopOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiErrorMessage,
  COMPETENCY_PERMS as P,
  competencyKeys,
  useCompetencyAction,
  useCompetencyCan,
  useCompetencyOverview,
  useTrainingCourses,
  useTrainingSchedule,
} from '@/hooks/useCompetency';
import { competencyService, type TrainingPlanItem } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { triggerDownload } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, MONTH_LABELS, PLAN_SOURCES, STATUS_COLOR, employeeLabel } from './competencyDisplay';

const { Text } = Typography;

const TrainingSchedulePage: React.FC = () => {
  const queryClient = useQueryClient();
  const canManage = useCompetencyCan(P.trainingManage);
  const canImport = useCompetencyCan(P.import);
  const canView = useCompetencyCan(P.trainingView);
  const canExport = canImport || canManage || canView;
  const [year, setYear] = useState(new Date().getFullYear());
  const [departmentId, setDepartmentId] = useState<string>();
  const [courseId, setCourseId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<'template' | 'export' | 'import' | null>(null);
  const [form] = Form.useForm();
  const target = Form.useWatch('target', form);

  const { data, isLoading, refetch } = useTrainingSchedule(year, { department_id: departmentId, course_id: courseId });
  const { data: courses = [] } = useTrainingCourses({ active: '1' });
  const { data: employees = [] } = useCompetencyOverview(undefined, canManage);
  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'selection'],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 300_000,
  });

  const add = useCompetencyAction((v: Record<string, unknown>) => competencyService.addScheduleItems(v), 'Schedule updated');
  const update = useCompetencyAction((v: { id: string; data: Record<string, unknown> }) => competencyService.updateScheduleItem(v.id, v.data));
  const remove = useCompetencyAction((id: string) => competencyService.deleteScheduleItem(id), 'Line deleted');

  const toggleMonth = (item: TrainingPlanItem, month: number) => {
    const months = item.planned_months.includes(month) ? item.planned_months.filter((m) => m !== month) : [...item.planned_months, month];
    update.mutate({ id: item.id, data: { planned_months: months } });
  };

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(await competencyService.downloadImportTemplate('schedule'), 'competency-schedule-template.xlsx');
      message.success('Template downloaded');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const exportCurrent = async () => {
    setBusy('export');
    try {
      triggerDownload(await competencyService.exportSchedule(year), `annual-training-schedule-${year}.xlsx`);
      message.success('Export started');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setBusy('import');
    try {
      const check = await competencyService.runImport('schedule', file, true);
      if (check.errors.length) {
        message.error(
          `Check failed: ${check.errors[0].message}${check.errors.length > 1 ? ` (+${check.errors.length - 1} more)` : ''}`
        );
        return false;
      }
      const result = await competencyService.runImport('schedule', file, false);
      message.success(`Imported: ${result.created} created, ${result.updated} updated`);
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      void refetch();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
    return false;
  };

  const monthColumns: ColumnsType<TrainingPlanItem> = MONTH_LABELS.map((label, i) => ({
    title: label,
    key: `m${i + 1}`,
    width: 56,
    align: 'center' as const,
    render: (_: unknown, r: TrainingPlanItem) => {
      const m = i + 1;
      const planned = r.planned_months.includes(m);
      const achieved = r.achieved_months.includes(m);
      const editable = canManage && r.status === 'Planned';
      return (
        <div
          onClick={editable ? () => toggleMonth(r, m) : undefined}
          style={{ cursor: editable ? 'pointer' : 'default', minHeight: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
        >
          {planned ? (
            <Tag color="blue" style={{ margin: 0 }}>
              P
            </Tag>
          ) : (
            <span style={{ color: '#d9d9d9' }}>.</span>
          )}
          {achieved ? (
            <Tag color="green" style={{ margin: 0 }}>
              A
            </Tag>
          ) : null}
        </div>
      );
    },
  }));

  const columns: ColumnsType<TrainingPlanItem> = [
    {
      title: 'Sl. No',
      width: 60,
      fixed: 'left',
      render: (_, __, i) => i + 1,
    },
    {
      title: 'Description or content of Training',
      dataIndex: 'course_title',
      fixed: 'left',
      width: 240,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          {r.course_content ? (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {r.course_content}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.course_code}
              {r.source_type !== 'TNA' ? ` | ${r.source_type}${r.source_ref ? ` ${r.source_ref}` : ''}` : ''}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Employee name',
      width: 180,
      render: (_, r) =>
        r.employee_id ? employeeLabel({ employee_number: r.employee_number, full_name: r.employee_name }) : <Tag>Whole department</Tag>,
    },
    {
      title: 'Employee department',
      dataIndex: 'department_name',
      width: 160,
      ellipsis: true,
      render: (v) => v || '-',
    },
    { title: 'Trainer', dataIndex: 'trainer', width: 130 },
    { title: 'Frequency of Training', dataIndex: 'frequency', width: 130 },
    ...monthColumns,
    { title: 'Status', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: '',
      width: 80,
      fixed: 'right',
      render: (_, r) =>
        canManage ? (
          <Space size={4}>
            {r.status === 'Planned' && (
              <Tooltip title="Cancel">
                <Button size="small" icon={<StopOutlined />} onClick={() => update.mutate({ id: r.id, data: { status: 'Cancelled' } })} />
              </Tooltip>
            )}
            <Popconfirm title="Delete this line?" onConfirm={() => remove.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={`Annual Training Schedule — ${year}`}
        extra={
          <Space wrap>
            <ReportDocumentActions
              reportKey="competency-training-schedule"
              params={{ year: String(year), department_id: departmentId ?? '', course_id: courseId ?? '' }}
              sendPermissions={[P.trainingManage]}
            />
            {canExport ? (
              <Button icon={<ExportOutlined />} loading={busy === 'export'} onClick={() => void exportCurrent()}>
                Export
              </Button>
            ) : null}
            {canImport ? (
              <>
                <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={() => void downloadTemplate()}>
                  Template
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(f) => {
                    void importFile(f);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={busy === 'import'}>
                    Import
                  </Button>
                </Upload>
                <Link to={`${COMPETENCY_BASE}/import?kind=schedule`}>
                  <Button icon={<ImportOutlined />}>Bulk import wizard</Button>
                </Link>
              </>
            ) : null}
            {canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  form.setFieldsValue({ target: 'employees', source_type: 'TNA', planned_months: [] });
                  setAdding(true);
                }}
              >
                Add training
              </Button>
            )}
          </Space>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <InputNumber value={year} min={2000} max={2100} onChange={(v) => v && setYear(Number(v))} addonBefore="YEAR" style={{ width: 160 }} />
          <Select
            allowClear
            placeholder="Employee department"
            style={{ width: 220 }}
            value={departmentId}
            onChange={setDepartmentId}
            showSearch
            optionFilterProp="label"
            options={departments.map((d) => ({ value: d.id, label: d.department_name }))}
          />
          <Select
            allowClear
            placeholder="Training"
            style={{ width: 260 }}
            value={courseId}
            onChange={setCourseId}
            showSearch
            optionFilterProp="label"
            options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
          />
          {data && (
            <Text>
              Planned {data.summary.planned}, achieved {data.summary.achieved}
              {data.summary.achievement_percent !== null ? ` (${data.summary.achievement_percent}%)` : ''}
            </Text>
          )}
        </Space>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
          Note: Training planned and achieved shall be marked with &quot;P&quot; and &quot;A&quot; against the planned and completed month. &quot;P&quot;
          Planned · &quot;A&quot; Achieved
          {canManage ? ' — Click a month to plan or unplan it.' : ''}
        </Text>
        {(data?.plan?.updated_at || data?.plan?.updated_by_name) && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Updated By: {data.plan.updated_by_name || '-'} · Last Updated Date: {formatDate(data.plan.updated_at) || '-'}
          </Text>
        )}
        <Table
          rowKey="id"
          size="small"
          bordered
          loading={isLoading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 1700 }}
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
        />
      </Card>

      <Modal
        title="Add training to the annual schedule"
        open={adding}
        width={720}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (v) => {
            await add.mutateAsync({
              year,
              course_id: v.course_id,
              employee_ids: v.target === 'employees' ? v.employee_ids : undefined,
              department_id: v.target === 'department' ? v.department_id : undefined,
              trainer: v.trainer,
              frequency: v.frequency,
              planned_months: v.planned_months,
              source_type: v.source_type,
              notes: v.notes,
            });
            setAdding(false);
          }}
        >
          <Form.Item name="course_id" label="Description or content of Training" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
              onChange={(cid) => {
                const c = courses.find((x) => x.id === cid);
                form.setFieldsValue({ trainer: c?.default_trainer ?? undefined, frequency: c?.frequency ?? undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="target" label="For">
            <Radio.Group
              options={[
                { value: 'employees', label: 'Selected employees (one line each)' },
                { value: 'department', label: 'A whole department (one line)' },
              ]}
            />
          </Form.Item>
          {target === 'department' ? (
            <Form.Item name="department_id" label="Employee department" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.department_name }))} />
            </Form.Item>
          ) : (
            <Form.Item name="employee_ids" label="Employee name" rules={[{ required: true, type: 'array', min: 1 }]}>
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                options={employees.map((e) => ({
                  value: e.id,
                  label: `${employeeLabel(e)}${e.department_name ? ` (${e.department_name})` : ''}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="planned_months"
            label="Planned months (P)"
            rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one month' }]}
          >
            <Checkbox.Group options={MONTH_LABELS.map((m, i) => ({ value: i + 1, label: m }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="trainer" label="Trainer">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="frequency" label="Frequency of Training">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source_type" label="Reason">
                <Select options={PLAN_SOURCES.map((s) => ({ value: s, label: s === 'TNA' ? 'Training need (TNA)' : s }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingSchedulePage;
